import { Injectable, computed, inject, signal } from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  RedirectRequest,
} from '@azure/msal-browser';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly msal = inject(MsalService);
  private readonly broadcast = inject(MsalBroadcastService);
  private readonly guardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private readonly destroy$ = new Subject<void>();

  private readonly _account = signal<AccountInfo | null>(null);
  readonly account = this._account.asReadonly();
  readonly isLoggedIn = computed(() => this._account() !== null);

  // True while MSAL is mid-interaction (redirect handling, login, token acquisition).
  // Guards against the `interaction_in_progress` BrowserAuthError.
  private readonly _inProgress = signal(true);
  readonly interactionInProgress = this._inProgress.asReadonly();
  readonly displayName = computed(() => {
    const acc = this._account();
    return acc?.name ?? acc?.username ?? '';
  });
  readonly initials = computed(() => {
    const name = this.displayName();
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    return parts.length === 1
      ? parts[0].slice(0, 2).toUpperCase()
      : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  });

  init(): void {
    this.msal.instance.enableAccountStorageEvents();
    this.refreshAccount();

    this.broadcast.msalSubject$
      .pipe(
        filter((m: EventMessage) =>
          m.eventType === EventType.LOGIN_SUCCESS ||
          m.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
          m.eventType === EventType.ACCOUNT_ADDED ||
          m.eventType === EventType.ACCOUNT_REMOVED ||
          m.eventType === EventType.LOGOUT_SUCCESS
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((m) => {
        if (m.eventType === EventType.LOGIN_SUCCESS && m.payload) {
          const payload = m.payload as AuthenticationResult;
          this.msal.instance.setActiveAccount(payload.account);
        }
        this.refreshAccount();
      });

    this.broadcast.inProgress$
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        this._inProgress.set(status !== InteractionStatus.None);
        if (status === InteractionStatus.None) {
          this.refreshAccount();
        }
      });
  }

  login(): void {
    // Avoid `interaction_in_progress` if a redirect is still being handled.
    if (this._inProgress()) {
      return;
    }
    const request: RedirectRequest = {
      ...(this.guardConfig.authRequest as RedirectRequest),
    };
    this.msal.loginRedirect(request);
  }

  logout(): void {
    this.msal.logoutRedirect({
      postLogoutRedirectUri: '/',
    });
  }

  private refreshAccount(): void {
    const active = this.msal.instance.getActiveAccount();
    if (active) {
      this._account.set(active);
      return;
    }
    const accounts = this.msal.instance.getAllAccounts();
    if (accounts.length > 0) {
      this.msal.instance.setActiveAccount(accounts[0]);
      this._account.set(accounts[0]);
    } else {
      this._account.set(null);
    }
  }
}
